import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const EMPTY_FORM = {
  client: null,
  site: null,
  siteName: '',
  description: '',
  portfolioSector: '',
  accounts: [],
  itemId: null,
};

const EMPTY_CONTRACT_FORM = {
  contract: null,
  accounts: [],
};

const getStaffLabel = (user = {}) => {
  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
  return fullName || user.email || 'Non renseigne';
};

const getStaffAvatarLabel = (user = {}) => {
  const first = String(user.firstname || '').trim();
  const last = String(user.lastname || '').trim();
  if (first || last) return `${first.slice(0, 1)}${last.slice(0, 2)}`.toUpperCase();
  return String(user.email || '?').slice(0, 3).toUpperCase();
};

const getEntityId = (entity = {}) => String(entity._id || '');

const normalizePortfolio = (portfolio = []) =>
  (Array.isArray(portfolio) ? portfolio : [])
    .map((entry, index) => ({
      user: entry.user?._id || entry.user || entry.userId,
      userDetails: entry.user,
      priority: Number(entry.priority || index + 1),
    }))
    .filter((entry) => entry.user)
    .sort((a, b) => a.priority - b.priority)
    .map((entry, index) => ({ ...entry, priority: index + 1 }));

const getPortfolioUser = (portfolio, priority) => {
  const entry = normalizePortfolio(portfolio).find((row) => row.priority === priority);
  return entry?.userDetails || null;
};

const getPortfolioUserId = (portfolio, priority) => {
  const entry = normalizePortfolio(portfolio).find((row) => row.priority === priority);
  return entry ? String(entry.user) : '';
};

const getPortfolioAccounts = (portfolio = []) => normalizePortfolio(portfolio)
  .map((entry) => entry.userDetails)
  .filter(Boolean);

const getPortfolioAccountEntries = (portfolio = []) => normalizePortfolio(portfolio)
  .map((entry) => ({ ...entry, user: entry.userDetails }))
  .filter((entry) => entry.user);

const buildPortfolioPayload = (accounts = []) => (Array.isArray(accounts) ? accounts : [])
  .filter(Boolean)
  .map((user, index) => ({
    user: user.value || user._id || user.id,
    priority: index + 1,
  }));

const findStaffAccounts = (portfolio = [], staffOptions = []) => normalizePortfolio(portfolio)
  .map((entry) => staffOptions.find((option) => option.value === String(entry.user)))
  .filter(Boolean);

function PortfolioAccountsCell({ portfolio, onClick, max = 5 }) {
  const entries = getPortfolioAccountEntries(portfolio);
  if (!entries.length) return <Typography variant="body2" color="text.secondary">-</Typography>;

  return (
    <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ py: 0.45, minWidth: 0 }}>
      {entries.slice(0, max).map((entry) => (
        <Stack key={`${entry.priority}-${entry.user?._id || entry.user?.email}`} direction="row" spacing={0.65} alignItems="center" sx={{ mr: 0.25, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 900, minWidth: 16, textAlign: 'right', lineHeight: 1 }}>{entry.priority}.</Typography>
          <StaffAvatar user={entry.user} onClick={onClick} size={28} fontSize="0.8rem" />
        </Stack>
      ))}
      {entries.length > max ? <Chip size="small" variant="outlined" label={`+${entries.length - max}`} /> : null}
    </Stack>
  );
}

const getGroupLabel = (entity = {}) => entity.portfolioGroup || '-';
const getSectorLabel = (entity = {}) => entity.portfolioSector || '';
const getCountryLabel = (entity = {}) => entity.address?.country || entity.country || '';

const CHART_COLORS = ['#0f766e', '#2563eb', '#f59e0b', '#ef4444', '#7c3aed', '#0891b2', '#84cc16', '#ec4899'];

const normalizeCountryLabel = (country) => {
  const raw = String(country || '').trim();
  const text = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (!text) return 'Non renseigné';
  if (text.includes('reunion')) return 'Réunion';
  if (text.includes('mayotte')) return 'Mayotte';
  if (text.includes('france')) return 'France';
  return raw;
};

const incrementMap = (map, key) => {
  const label = key || 'Non renseigné';
  map.set(label, (map.get(label) || 0) + 1);
};

const mapToChartData = (map) => Array.from(map.entries())
  .map(([name, value]) => ({ name, value }))
  .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
const getPortfolioRowSector = (row = {}) => row.item?.portfolioSector || row.client?.portfolioSector || '';

const buildStaffOption = (user) => ({
  ...user,
  value: String(user._id),
  label: getStaffLabel(user),
});

const colorForIndex = (index) => {
  const colors = ['#2f5597', '#4472c4', '#1f4e79', '#5b9bd5', '#385723', '#7030a0'];
  return colors[index % colors.length];
};

const getSiteLabel = (item) => item.site?.name || item.siteName || 'General';

const getContractLabel = (contract = {}) => contract.reference || contract.name || 'Contrat sans nom';

const getOrganizationLabel = (row) => row?.organization?.name || row?.entity?.Organization?.name || '-';

const ROOT_LABELS = ['TOOLBOX', 'MAYBOX', 'CLOUDBOX'];
const SECTOR_OPTIONS = ['Nord', 'Est', 'Sud', 'Ouest', 'Cloud', 'Mayotte'];

const normalizeUpper = (value) => String(value || '').trim().toUpperCase();

const getCanonicalRootLabel = (value) => {
  const text = normalizeUpper(value);
  if (!text) return '';
  if (text.includes('CLOUDBOX') || text.includes('CLOUD')) return 'CLOUDBOX';
  if (text.includes('MAYBOX') || text.includes('MAY')) return 'MAYBOX';
  if (text.includes('TOOLBOX') || text.includes('TOOL')) return 'TOOLBOX';
  return '';
};

const getRootLabels = (entity = {}) => {
  const roots = [];
  const addRoot = (root) => {
    if (!root) return;
    roots.push(getCanonicalRootLabel(root.name || root.parent_name || root));
  };

  if (Array.isArray(entity.organizationRoots)) entity.organizationRoots.forEach(addRoot);
  addRoot(entity.organizationRoot);
  addRoot(entity.parent_name);

  return [...new Set(roots.filter(Boolean))];
};

const getStaffOrganizationLabel = (user = {}) => {
  const organizationName = user.Organization?.name || user.Organization?.parent_name || '';
  const email = String(user.email || '').toLowerCase();
  const derived = organizationName
    || (email.endsWith('@cloudbox.re') ? 'CLOUDBOX' : '')
    || (email.endsWith('@maybox.yt') ? 'MAYBOX' : '')
    || (email.endsWith('@toolbox.re') ? 'TOOLBOX' : '');
  return getCanonicalRootLabel(derived) || 'AUTRES';
};

const getPortfolioRole = (portfolio, userId) => {
  if (!userId || userId === 'all') return null;
  const entry = normalizePortfolio(portfolio).find((row) => String(row.user) === String(userId));
  if (!entry) return null;
  return entry.priority === 1 ? 'primary' : `rank-${entry.priority}`;
};

const getRoleLabel = (role) => {
  if (role === 'primary') return 'N° 1';
  const match = String(role || '').match(/^rank-(\d+)$/);
  return match ? `N° ${match[1]}` : '';
};
const getRoleRank = (role) => {
  if (role === 'primary') return 0;
  const match = String(role || '').match(/^rank-(\d+)$/);
  return match ? Number(match[1]) - 1 : 999;
};

const formatClientCount = (count, suffix) => `${count} client${count > 1 ? 's' : ''} ${suffix}`;

const findInheritedPortfolioRole = (row, userId) => {
  const items = Array.isArray(row?.inheritedClientPortfolioItems) && row.inheritedClientPortfolioItems.length
    ? row.inheritedClientPortfolioItems
    : [{ staffPortfolio: row?.inheritedClientPortfolio || [] }];

  for (const item of items) {
    const role = getPortfolioRole(item.staffPortfolio, userId);
    if (role) return role;
  }

  return null;
};

function StaffAvatar({ user, onClick, size = 28, fontSize = '0.78rem' }) {
  const userId = String(user?._id || user?.id || user?.value || '');
  const clickable = Boolean(onClick && userId);

  return (
    <Tooltip title={clickable ? `Filtrer sur ${getStaffLabel(user)}` : getStaffLabel(user)}>
      <Avatar
        component={clickable ? 'button' : 'div'}
        type={clickable ? 'button' : undefined}
        onClick={clickable ? () => onClick(userId) : undefined}
        sx={{
          width: size,
          height: size,
          border: '1px solid',
          borderColor: clickable ? 'primary.main' : 'rgba(255,255,255,0.7)',
          bgcolor: clickable ? 'primary.main' : 'rgba(255,255,255,0.22)',
          color: clickable ? 'primary.contrastText' : '#fff',
          fontSize,
          fontWeight: 900,
          cursor: clickable ? 'pointer' : 'default',
          p: 0,
        }}
      >
        {getStaffAvatarLabel(user)}
      </Avatar>
    </Tooltip>
  );
}

function ManagerCell({ user, fallback = '-', onClick }) {
  if (!user) return <Typography variant="body2" color="text.secondary">{fallback}</Typography>;
  return <StaffAvatar user={user} onClick={onClick} />;
}

function RoleChip({ role }) {
  if (!role) return null;

  return (
    <Chip
      size="small"
      label={getRoleLabel(role)}
      color={role === 'primary' ? 'success' : 'info'}
      variant={role === 'primary' ? 'filled' : 'outlined'}
      sx={{ height: 20, fontSize: '0.66rem', fontWeight: 800 }}
    />
  );
}


function PortfolioPieCard({ title, data }) {
  const total = data.reduce((sum, item) => sum + Number(item.value || 0), 0);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: 260 }}>
      <Stack spacing={1} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
          <Chip size="small" label={total} variant="outlined" />
        </Stack>
        {data.length ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="48%"
                  innerRadius={38}
                  outerRadius={72}
                  paddingAngle={2}
                >
                  {data.map((entry, index) => (
                    <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value, name) => [`${value}`, name]} />
                <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}


function PortfolioStackedBarCard({ data }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: 260 }}>
      <Stack spacing={1} sx={{ height: '100%' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Positions par compte</Typography>
        {data.length ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={48} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="principal" name="Position 1" stackId="clients" fill="#0f766e" radius={[3, 3, 0, 0]} />
                <Bar dataKey="secondaire" name="Positions suivantes" stackId="clients" fill="#2563eb" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function PortfolioMatrix({ title, user, primaryCount = 0, secondaryCount = 0, rows, color, onEdit, onClear, onManagerClick, editingSectorRowId, onSectorEditStart, onSectorChange, showRole = false }) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderColor: 'divider' }}>
      <Box sx={{ bgcolor: color, color: '#fff', px: 1.5, py: 0.9 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            {user ? <StaffAvatar user={user} size={32} fontSize="0.84rem" /> : null}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>{title}</Typography>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.2 }} noWrap>
                {formatClientCount(primaryCount, 'position 1')} - {formatClientCount(secondaryCount, 'suivants')}
              </Typography>
            </Box>
          </Stack>
          <Chip size="small" label={`${rows.length} ligne${rows.length > 1 ? 's' : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
        </Stack>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#eaf2f8' }}>
              {showRole ? <TableCell sx={{ width: 105, fontWeight: 700 }}>Rôle</TableCell> : null}
              <TableCell sx={{ width: 90, fontWeight: 700 }}>Groupe</TableCell>
              <TableCell sx={{ width: '25%', fontWeight: 700 }}>Client</TableCell>
              <TableCell sx={{ width: 110, fontWeight: 700 }}>Pays</TableCell>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Secteur</TableCell>
              <TableCell sx={{ width: 210, fontWeight: 700 }}>Comptes</TableCell>
              <TableCell align="right" sx={{ width: 80, fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.rowId} hover>
                {showRole ? <TableCell><RoleChip role={row.matchRole} /></TableCell> : null}
                <TableCell><Typography variant="body2" noWrap>{getGroupLabel(row.client)}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {row.client.name || 'Sans nom'}{getSiteLabel(row.item) && getSiteLabel(row.item) !== 'General' ? ` (${getSiteLabel(row.item)})` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={getCountryLabel(row.client) ? 'text.primary' : 'text.secondary'} noWrap>
                    {getCountryLabel(row.client) || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {editingSectorRowId === row.rowId ? (
                    <Autocomplete
                      autoFocus
                      openOnFocus
                      size="small"
                      options={['', ...SECTOR_OPTIONS]}
                      value={getPortfolioRowSector(row)}
                      getOptionLabel={(option) => option || '-'}
                      onChange={(_, value) => onSectorChange(row, value || '')}
                      onBlur={() => onSectorEditStart('')}
                      sx={{ width: 130 }}
                      renderInput={(params) => <TextField {...params} placeholder="Secteur" />}
                    />
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {getPortfolioRowSector(row) ? (
                        <Chip size="small" variant="outlined" label={getPortfolioRowSector(row)} sx={{ height: 22, fontSize: '0.72rem' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                      <Tooltip title="Modifier le secteur">
                        <IconButton size="small" onClick={() => onSectorEditStart(row.rowId)} sx={{ p: 0.25 }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
                <TableCell><PortfolioAccountsCell portfolio={row.item.staffPortfolio} onClick={onManagerClick} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => onEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {row.item._id ? (
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => onClear(row)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function InheritedPortfolioCell({ row }) {
  const items = Array.isArray(row.inheritedClientPortfolioItems) && row.inheritedClientPortfolioItems.length
    ? row.inheritedClientPortfolioItems
    : [{ siteName: 'Client', description: '', staffPortfolio: row.inheritedClientPortfolio || [] }];
  const visibleItems = items.filter((item) => normalizePortfolio(item.staffPortfolio).length).slice(0, 2);

  if (!visibleItems.length) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  return (
    <Stack spacing={0.5}>
      {visibleItems.map((item, index) => {
        const accounts = getPortfolioAccounts(item.staffPortfolio);
        const label = item.site?.name || item.siteName || item.description || 'Client';

        return (
          <Box key={item._id || `${label}-${index}`}>
            <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }} noWrap>
              {accounts.map((user, userIndex) => `${userIndex + 1}. ${getStaffLabel(user)}`).join(' / ')}
            </Typography>
          </Box>
        );
      })}
      {items.length > visibleItems.length ? (
        <Typography variant="caption" color="text.secondary">+{items.length - visibleItems.length} autre{items.length - visibleItems.length > 1 ? 's' : ''}</Typography>
      ) : null}
    </Stack>
  );
}

function ContractPortfolioTable({ rows, onEdit, onManagerClick, showRole = false }) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#eaf2f8' }}>
              {showRole ? <TableCell sx={{ width: 115, fontWeight: 700 }}>Rôle</TableCell> : null}
              <TableCell sx={{ width: '21%', fontWeight: 700 }}>Contrat</TableCell>
              <TableCell sx={{ width: '18%', fontWeight: 700 }}>Client</TableCell>
              <TableCell sx={{ width: '11%', fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ width: 220, fontWeight: 700 }}>Comptes contrat</TableCell>
              <TableCell sx={{ width: '24%', fontWeight: 700 }}>Portefeuille client</TableCell>
              <TableCell align="right" sx={{ width: 80, fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const contract = row.entity || {};

              return (
                <TableRow key={getEntityId(contract)} hover>
                  {showRole ? <TableCell><RoleChip role={row.matchRole} /></TableCell> : null}
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{getContractLabel(contract)}</Typography>
                      {contract.name && contract.name !== contract.reference ? (
                        <Typography variant="caption" color="text.secondary" noWrap>{contract.name}</Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2" noWrap>{getOrganizationLabel(row)}</Typography></TableCell>
                  <TableCell><Typography variant="body2" noWrap>{contract.contractType || '-'}</Typography></TableCell>
                  <TableCell><PortfolioAccountsCell portfolio={contract.staffPortfolio} onClick={onManagerClick} /></TableCell>
                  <TableCell><InheritedPortfolioCell row={row} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => onEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function StaffPortfolioPage() {
  const [summary, setSummary] = useState({ organizations: [], contracts: [] });
  const [staffUsers, setStaffUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('clients');
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [contractAssignmentOpen, setContractAssignmentOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [contractForm, setContractForm] = useState(EMPTY_CONTRACT_FORM);
  const [editingSectorRowId, setEditingSectorRowId] = useState('');
  const [siteOptions, setSiteOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [rootFilter, setRootFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const staffOptions = useMemo(
    () => staffUsers
      .map((user) => ({ ...buildStaffOption(user), organizationLabel: getStaffOrganizationLabel(user) }))
      .sort((a, b) => {
        const orgSort = a.organizationLabel.localeCompare(b.organizationLabel);
        if (orgSort !== 0) return orgSort;
        return a.label.localeCompare(b.label);
      }),
    [staffUsers]
  );

  const clientOptions = useMemo(
    () => (summary.organizations || [])
      .map((row) => row.entity)
      .filter(Boolean)
      .map((entity) => ({ ...entity, value: getEntityId(entity), label: entity.name || 'Sans nom' })),
    [summary.organizations]
  );

  const rootOptions = useMemo(() => ROOT_LABELS, []);
  const countryOptions = useMemo(() => Array.from(new Set(clientOptions.map((client) => normalizeCountryLabel(getCountryLabel(client))).filter(Boolean))).sort(), [clientOptions]);

  const portfolioRows = useMemo(() => {
    const rows = [];

    clientOptions.forEach((client) => {
      const items = Array.isArray(client.staffPortfolioItems) ? client.staffPortfolioItems : [];

      if (items.length) {
        items.forEach((item) => {
          rows.push({
            rowId: `${client.value}-${item._id}`,
            client,
            item,
          });
        });
        return;
      }

      const legacyPortfolio = normalizePortfolio(client.staffPortfolio);
      if (legacyPortfolio.length) {
        rows.push({
          rowId: `${client.value}-legacy`,
          client,
          item: {
            _id: null,
            site: null,
            siteName: '',
            description: '',
            staffPortfolio: legacyPortfolio,
          },
        });
      }
    });

    return rows;
  }, [clientOptions]);

  const selectedClientExistingRows = useMemo(() => {
    if (!form.client?.value) return [];
    return portfolioRows.filter((row) => row.client.value === form.client.value && row.item._id !== form.itemId);
  }, [form.client?.value, form.itemId, portfolioRows]);

  const portfolioChartData = useMemo(() => {
    const principalMap = new Map();
    const sectorMap = new Map();
    const countryMap = new Map();

    portfolioRows.forEach((row) => {
      const first = getPortfolioUser(row.item.staffPortfolio, 1);
      incrementMap(principalMap, first ? getStaffLabel(first) : 'Sans compte');
      incrementMap(sectorMap, getPortfolioRowSector(row) || 'Sans secteur');
      incrementMap(countryMap, normalizeCountryLabel(getCountryLabel(row.client)));
    });

    const accountCounts = new Map();
    const ensureAccount = (user) => {
      const id = String(user?._id || user?.id || user?.value || 'unassigned');
      if (!accountCounts.has(id)) {
        accountCounts.set(id, { id, label: user ? getStaffAvatarLabel(user) : 'Sans', name: user ? getStaffLabel(user) : 'Sans compte', principal: 0, secondaire: 0 });
      }
      return accountCounts.get(id);
    };

    portfolioRows.forEach((row) => {
      getPortfolioAccountEntries(row.item.staffPortfolio).forEach((entry) => {
        if (entry.priority === 1) ensureAccount(entry.user).principal += 1;
        else ensureAccount(entry.user).secondaire += 1;
      });
    });

    return {
      principal: mapToChartData(principalMap),
      sector: mapToChartData(sectorMap),
      country: mapToChartData(countryMap),
      accounts: Array.from(accountCounts.values()).sort((a, b) => (b.principal + b.secondaire) - (a.principal + a.secondaire) || a.name.localeCompare(b.name)),
    };
  }, [portfolioRows]);

  const contractRows = useMemo(
    () => (summary.contracts || []).filter((row) => row?.entity?._id),
    [summary.contracts]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return portfolioRows
      .map((row) => ({ ...row, matchRole: getPortfolioRole(row.item.staffPortfolio, managerFilter) }))
      .filter((row) => {
        const text = [
          row.client.name,
          getGroupLabel(row.client),
          getSectorLabel(row.client),
          getCountryLabel(row.client),
          getSiteLabel(row.item),
          row.item.description,
        ].join(' ').toLowerCase();
        const matchesSearch = !normalizedSearch || text.includes(normalizedSearch);
        const matchesClient = clientFilter === 'all' || row.client.value === clientFilter;
        const matchesRoot = rootFilter === 'all' || getRootLabels(row.client).includes(rootFilter);
        const matchesSector = sectorFilter === 'all' || getPortfolioRowSector(row) === sectorFilter;
        const matchesCountry = countryFilter === 'all' || normalizeCountryLabel(getCountryLabel(row.client)) === countryFilter;
        const matchesManager = managerFilter === 'all' || !!row.matchRole;

        return matchesSearch && matchesClient && matchesRoot && matchesSector && matchesCountry && matchesManager;
      })
      .sort((a, b) => {
        if (managerFilter !== 'all') {
          const roleSort = getRoleRank(a.matchRole) - getRoleRank(b.matchRole);
          if (roleSort !== 0) return roleSort;
        }
        return (a.client.name || '').localeCompare(b.client.name || '');
      });
  }, [clientFilter, countryFilter, managerFilter, portfolioRows, rootFilter, search, sectorFilter]);

  const filteredContractRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return contractRows
      .map((row) => {
        const contract = row.entity || {};
        const directRole = getPortfolioRole(contract.staffPortfolio, managerFilter);
        const inheritedRole = directRole ? null : findInheritedPortfolioRole(row, managerFilter);
        return { ...row, matchRole: directRole || inheritedRole, matchRoleSource: directRole ? 'contract' : inheritedRole ? 'client' : null };
      })
      .filter((row) => {
        const contract = row.entity || {};
        const text = [
          contract.reference,
          contract.name,
          contract.contractType,
          getOrganizationLabel(row),
        ].join(' ').toLowerCase();
        const orgId = String(row.organization?._id || contract.Organization?._id || contract.Organization || '');
        const orgEntity = row.organization || contract.Organization || {};
        const matchesSearch = !normalizedSearch || text.includes(normalizedSearch);
        const matchesClient = clientFilter === 'all' || orgId === clientFilter;
        const matchesRoot = rootFilter === 'all' || getRootLabels(orgEntity).includes(rootFilter);
        const matchesSector = sectorFilter === 'all' || getSectorLabel(orgEntity) === sectorFilter;
        const matchesCountry = countryFilter === 'all' || normalizeCountryLabel(getCountryLabel(orgEntity)) === countryFilter;
        const matchesManager = managerFilter === 'all' || !!row.matchRole;

        return matchesSearch && matchesClient && matchesRoot && matchesSector && matchesCountry && matchesManager;
      })
      .sort((a, b) => {
        if (managerFilter !== 'all') {
          const roleSort = getRoleRank(a.matchRole) - getRoleRank(b.matchRole);
          if (roleSort !== 0) return roleSort;
        }
        return getContractLabel(a.entity || {}).localeCompare(getContractLabel(b.entity || {}));
      });
  }, [clientFilter, contractRows, countryFilter, managerFilter, rootFilter, search, sectorFilter]);

  const groupedPortfolios = useMemo(() => {
    const groups = new Map();
    const countsByUser = new Map();
    const ensureCounts = (userId, user) => {
      if (!userId) return null;
      if (!countsByUser.has(userId)) countsByUser.set(userId, { user, primary: 0, secondary: 0 });
      const counts = countsByUser.get(userId);
      if (!counts.user && user) counts.user = user;
      return counts;
    };

    filteredRows.forEach((row) => {
      getPortfolioAccountEntries(row.item.staffPortfolio).forEach((entry) => {
        const userId = String(entry.user?._id || entry.user?.id || '');
        if (!userId) return;
        const counts = ensureCounts(userId, entry.user);
        if (entry.priority === 1) counts.primary += 1;
        else counts.secondary += 1;
      });
    });

    const selectedUser = staffOptions.find((staff) => staff.value === managerFilter) || null;

    filteredRows.forEach((row) => {
      if (managerFilter !== 'all') {
        const role = row.matchRole || 'rank-2';
        const id = role;
        const counts = countsByUser.get(managerFilter) || { user: selectedUser, primary: 0, secondary: 0 };
        const title = `${selectedUser ? getStaffLabel(selectedUser) : 'Compte'} - ${getRoleLabel(role)}`;

        if (!groups.has(id)) {
          groups.set(id, { id, title, user: counts.user || selectedUser, primaryCount: counts.primary, secondaryCount: counts.secondary, rows: [] });
        }

        groups.get(id).rows.push(row);
        return;
      }

      const primary = getPortfolioUser(row.item.staffPortfolio, 1);
      const primaryId = getPortfolioUserId(row.item.staffPortfolio, 1) || 'unassigned';
      const counts = countsByUser.get(primaryId) || { user: primary, primary: 0, secondary: 0 };
      const title = primary ? getStaffLabel(primary) : 'Sans compte';

      if (!groups.has(primaryId)) {
        groups.set(primaryId, { id: primaryId, title, user: primary, primaryCount: counts.primary, secondaryCount: counts.secondary, rows: [] });
      }

      groups.get(primaryId).rows.push(row);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (managerFilter !== 'all') return getRoleRank(a.id) - getRoleRank(b.id);
      if (a.id === 'unassigned') return 1;
      if (b.id === 'unassigned') return -1;
      return a.title.localeCompare(b.title);
    });
  }, [filteredRows, managerFilter, staffOptions]);

  const stats = useMemo(() => ({
    totalClients: clientOptions.length,
    totalRows: portfolioRows.length,
    detailedRows: portfolioRows.filter((row) => row.item._id).length,
    totalContracts: contractRows.length,
    assignedContracts: contractRows.filter((row) => normalizePortfolio(row.entity?.staffPortfolio).length).length,
  }), [clientOptions.length, contractRows, portfolioRows]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryResponse, staffResponse] = await Promise.all([
        api.get('/api/staff-portfolio/summary'),
        api.get('/api/staff-portfolio/staff-options'),
      ]);
      setSummary(summaryResponse.data || { organizations: [], contracts: [] });
      setStaffUsers(Array.isArray(staffResponse.data) ? staffResponse.data : []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadSites = async (clientId) => {
    if (!clientId) {
      setSiteOptions([]);
      return;
    }

    setSitesLoading(true);
    try {
      const { data } = await api.get(`/api/staff-portfolio/organizations/${clientId}/sites`);
      setSiteOptions((Array.isArray(data) ? data : []).map((site) => ({
        ...site,
        value: String(site._id),
        label: site.address ? `${site.name || 'Site'} - ${site.address}` : site.name || 'Site',
      })));
    } catch (e) {
      setSiteOptions([]);
    } finally {
      setSitesLoading(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setSiteOptions([]);
  };

  const closeAssignment = () => {
    if (saving) return;
    setAssignmentOpen(false);
    resetForm();
  };

  const closeContractAssignment = () => {
    if (saving) return;
    setContractAssignmentOpen(false);
    setContractForm(EMPTY_CONTRACT_FORM);
  };

  const openAssignment = () => {
    resetForm();
    setAssignmentOpen(true);
  };

  const openEdit = (row) => {
    const accounts = findStaffAccounts(row.item.staffPortfolio, staffOptions);
    const client = clientOptions.find((option) => option.value === row.client.value) || row.client;

    setForm({
      client,
      site: row.item.site?._id ? { ...row.item.site, value: String(row.item.site._id), label: row.item.site.address ? `${row.item.site.name || 'Site'} - ${row.item.site.address}` : row.item.site.name || 'Site' } : null,
      siteName: row.item.siteName || row.item.site?.name || '',
      description: row.item.description || '',
      portfolioSector: row.item.portfolioSector || getSectorLabel(client),
      accounts,
      itemId: row.item._id || null,
    });
    loadSites(client.value);
    setAssignmentOpen(true);
  };

  const openContractEdit = (row) => {
    const contract = row.entity || {};
    const accounts = findStaffAccounts(contract.staffPortfolio, staffOptions);

    setContractForm({
      contract: row,
      accounts,
    });
    setContractAssignmentOpen(true);
  };

  const saveContractPortfolio = async () => {
    const contractId = getEntityId(contractForm.contract?.entity);
    if (!contractId || !contractForm.accounts.length) {
      setError('Contrat et au moins un compte requis.');
      return;
    }

    setSaving(true);
    setSuccess('');
    try {
      await api.put(`/api/staff-portfolio/contracts/${contractId}`, {
        staffPortfolio: buildPortfolioPayload(contractForm.accounts),
      });

      setSuccess(`Portefeuille contrat mis a jour pour ${getContractLabel(contractForm.contract?.entity)}`);
      setContractAssignmentOpen(false);
      setContractForm(EMPTY_CONTRACT_FORM);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const saveSectorInline = async (row, portfolioSector) => {
    if (!row?.client?.value) return;

    setSaving(true);
    setSuccess('');
    try {
      if (row.item?._id) {
        await api.put(`/api/staff-portfolio/organizations/${row.client.value}/items/${row.item._id}`, {
          siteId: row.item.site?._id || row.item.site || null,
          siteName: row.item.siteName || row.item.site?.name || '',
          description: row.item.description || '',
          portfolioSector,
          staffPortfolio: normalizePortfolio(row.item.staffPortfolio).map((entry) => ({
            user: entry.user,
            priority: entry.priority,
          })),
        });
      } else {
        await api.put(`/api/staff-portfolio/organizations/${row.client.value}`, { portfolioSector });
      }

      setEditingSectorRowId('');
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur lors de la mise a jour du secteur');
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async () => {
    if (!form.client?.value || !form.accounts.length) {
      setError('Client et au moins un compte requis.');
      return;
    }

    setSaving(true);
    setSuccess('');
    try {
      const payload = {
        siteId: form.site?.value || null,
        siteName: form.siteName,
        description: form.description,
        portfolioSector: form.portfolioSector || '',
        staffPortfolio: buildPortfolioPayload(form.accounts),
      };

      if (form.itemId) {
        await api.put(`/api/staff-portfolio/organizations/${form.client.value}/items/${form.itemId}`, payload);
      } else {
        await api.post(`/api/staff-portfolio/organizations/${form.client.value}/items`, payload);
      }

      setSuccess(`Ligne portefeuille mise a jour pour ${form.client.name || form.client.label}`);
      setAssignmentOpen(false);
      resetForm();
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (row) => {
    if (!row.item._id) return;

    setSaving(true);
    try {
      await api.delete(`/api/staff-portfolio/organizations/${row.client.value}/items/${row.item._id}`);
      setSuccess('Ligne portefeuille supprimee.');
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };


  const getAccountsExportText = (portfolio = []) => getPortfolioAccounts(portfolio)
    .map((user, index) => `${index + 1}. ${getStaffLabel(user)}`)
    .join(' | ');

  const getInheritedPortfolioExportText = (row) => {
    const inheritedItems = Array.isArray(row.inheritedClientPortfolioItems) && row.inheritedClientPortfolioItems.length
      ? row.inheritedClientPortfolioItems
      : [{ siteName: 'Client', staffPortfolio: row.inheritedClientPortfolio || [] }];

    return inheritedItems
      .filter((item) => normalizePortfolio(item.staffPortfolio).length)
      .map((item) => {
        const siteLabel = item.site?.name || item.siteName || item.description || 'Client';
        return `${siteLabel}: ${getAccountsExportText(item.staffPortfolio)}`;
      })
      .join(' / ');
  };

  const getExportRowsByAccount = () => {
    const rows = [];

    if (activeTab === 'contracts') {
      filteredContractRows.forEach((row) => {
        const contract = row.entity || {};
        const contractAccounts = getPortfolioAccountEntries(contract.staffPortfolio);
        const inheritedItems = Array.isArray(row.inheritedClientPortfolioItems) && row.inheritedClientPortfolioItems.length
          ? row.inheritedClientPortfolioItems
          : [{ siteName: 'Client', staffPortfolio: row.inheritedClientPortfolio || [] }];
        const inheritedAccounts = inheritedItems.flatMap((item) => getPortfolioAccountEntries(item.staffPortfolio).map((entry) => ({
          ...entry,
          siteLabel: item.site?.name || item.siteName || item.description || 'Client',
        })));

        [...contractAccounts.map((entry) => ({ ...entry, source: 'Contrat' })), ...inheritedAccounts.map((entry) => ({ ...entry, source: 'Portefeuille client' }))]
          .forEach((entry) => {
            rows.push({
              Compte: getStaffLabel(entry.user),
              Ordre: entry.priority || '',
              Source: entry.source,
              Contrat: getContractLabel(contract),
              'Référence': contract.reference || '',
              Client: getOrganizationLabel(row),
              Site: entry.siteLabel || '',
              Type: contract.contractType || '',
              'Comptes contrat': getAccountsExportText(contract.staffPortfolio),
              'Portefeuille client': getInheritedPortfolioExportText(row),
            });
          });
      });
      return rows.sort((a, b) => `${a.Compte} ${a.Client} ${a.Contrat}`.localeCompare(`${b.Compte} ${b.Client} ${b.Contrat}`));
    }

    filteredRows.forEach((row) => {
      getPortfolioAccountEntries(row.item.staffPortfolio).forEach((entry) => {
        rows.push({
          Compte: getStaffLabel(entry.user),
          Ordre: entry.priority || '',
          Groupe: getGroupLabel(row.client),
          Client: row.client.name || '',
          Site: getSiteLabel(row.item),
          Pays: getCountryLabel(row.client) || '',
          Secteur: getPortfolioRowSector(row) || '',
          'Comptes client': getAccountsExportText(row.item.staffPortfolio),
        });
      });
    });

    return rows.sort((a, b) => `${a.Compte} ${a.Client} ${a.Site}`.localeCompare(`${b.Compte} ${b.Client} ${b.Site}`));
  };

  const getExportRows = (exportMode = 'current') => {
    if (exportMode === 'account') return getExportRowsByAccount();

    if (activeTab === 'contracts') {
      return filteredContractRows.map((row) => {
        const contract = row.entity || {};
        return {
          Contrat: getContractLabel(contract),
          'Référence': contract.reference || '',
          Client: getOrganizationLabel(row),
          Type: contract.contractType || '',
          'Comptes contrat': getAccountsExportText(contract.staffPortfolio),
          'Portefeuille client': getInheritedPortfolioExportText(row),
        };
      });
    }

    return filteredRows.map((row) => ({
      Groupe: getGroupLabel(row.client),
      Client: row.client.name || '',
      Site: getSiteLabel(row.item),
      Pays: getCountryLabel(row.client) || '',
      Secteur: getPortfolioRowSector(row) || '',
      Comptes: getAccountsExportText(row.item.staffPortfolio),
    }));
  };

  const getExportTitle = (exportMode = 'current') => {
    const target = activeTab === 'contracts' ? 'contrats' : 'clients';
    return exportMode === 'account' ? `Portefeuille ${target} par compte` : `Portefeuille ${target}`;
  };

  const getExportFilename = (exportMode = 'current') => {
    const target = activeTab === 'contracts' ? 'contrats' : 'clients';
    return exportMode === 'account' ? `portefeuille-${target}-par-compte` : `portefeuille-${target}`;
  };

  const exportCsv = (exportMode = 'current') => {
    const rows = getExportRows(exportMode);
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.join(';'), ...rows.map((row) => headers.map((header) => escapeCsv(row[header])).join(';'))].join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${getExportFilename(exportMode)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPdf = (exportMode = 'current') => {
    const rows = getExportRows(exportMode);
    const headers = rows.length ? Object.keys(rows[0]) : [];
    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const accountShort = (user) => (user ? getStaffAvatarLabel(user) : '-');
    const renderAccountSection = (title, sectionRows, sectionHeaders) => {
      const renderCategory = (label, rows) => rows.length
        ? `<tr class="category-row"><td colspan="${sectionHeaders.length}">${escapeHtml(label)}</td></tr>${rows.join('')}`
        : '';
      const primaryRows = sectionRows.filter((item) => item.priority === 1).map((item) => item.html);
      const secondaryRows = sectionRows.filter((item) => item.priority !== 1).map((item) => item.html);
      const body = renderCategory('Principal', primaryRows) + renderCategory('Secondaire', secondaryRows);

      return `
      <section class="account-section">
        <div class="account-title">${escapeHtml(title)}</div>
        <table class="portfolio-grid">
          <thead><tr>${sectionHeaders.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr></thead>
          <tbody>${body || `<tr><td colspan="${sectionHeaders.length}">Aucune donnée</td></tr>`}</tbody>
        </table>
      </section>`;
    };

    const renderAccountPdfBody = () => {
      if (activeTab === 'contracts') {
        const groups = new Map();
        filteredContractRows.forEach((row) => {
          const contract = row.entity || {};
          const contractEntries = getPortfolioAccountEntries(contract.staffPortfolio);
          const inheritedItems = Array.isArray(row.inheritedClientPortfolioItems) && row.inheritedClientPortfolioItems.length
            ? row.inheritedClientPortfolioItems
            : [{ siteName: 'Client', staffPortfolio: row.inheritedClientPortfolio || [] }];
          const inheritedEntries = inheritedItems.flatMap((item) => getPortfolioAccountEntries(item.staffPortfolio).map((entry) => ({
            ...entry,
            source: 'Client',
            siteLabel: item.site?.name || item.siteName || item.description || '',
            accounts: getPortfolioAccounts(item.staffPortfolio),
          })));
          const entries = [
            ...contractEntries.map((entry) => ({ ...entry, source: 'Contrat', siteLabel: '', accounts: contractEntries.map((contractEntry) => contractEntry.user) })),
            ...inheritedEntries,
          ];
          entries.forEach((entry) => {
            const label = getStaffLabel(entry.user);
            if (!groups.has(label)) groups.set(label, []);
            groups.get(label).push({ row, contract, entry, contractEntries });
          });
        });

        const sectionHeaders = ['Contrat', 'Référence', 'Client', 'Type', 'Source', 'Cpte principal', 'Binôme', 'Autres comptes'];
        return [...groups.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([label, groupRows]) => renderAccountSection(`${label} ${new Date().getFullYear()}`, groupRows.map(({ row, contract, entry }) => {
            const accounts = entry.accounts || [];
            const primary = accounts[0];
            const secondary = accounts[1];
            const others = accounts.slice(2).map(accountShort).join(', ');
            return {
              priority: entry.priority || 99,
              html: `<tr>
                <td>${escapeHtml(getContractLabel(contract))}</td>
                <td>${escapeHtml(contract.reference || '')}</td>
                <td>${escapeHtml(getOrganizationLabel(row))}</td>
                <td>${escapeHtml(contract.contractType || '')}</td>
                <td>${escapeHtml(entry.source)}</td>
                <td class="account-code">${escapeHtml(accountShort(primary))}</td>
                <td class="account-code">${escapeHtml(accountShort(secondary))}</td>
                <td>${escapeHtml(others)}</td>
              </tr>`,
            };
          }), sectionHeaders))
          .join('');
      }

      const groups = new Map();
      filteredRows.forEach((row) => {
        const entries = getPortfolioAccountEntries(row.item.staffPortfolio);
        entries.forEach((entry) => {
          const label = getStaffLabel(entry.user);
          if (!groups.has(label)) groups.set(label, []);
          groups.get(label).push({ row, entries });
        });
      });

      const sectionHeaders = ['Groupe', 'Clients', 'Secteurs', 'Cpte principal', 'Binôme', 'Autres comptes'];
      return [...groups.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([label, groupRows]) => renderAccountSection(`${label} ${new Date().getFullYear()}`, groupRows.map(({ row, entries }) => {
          const accounts = entries.map((entry) => entry.user);
          const currentEntry = entries.find((entry) => getStaffLabel(entry.user) === label);
          const primary = accounts[0];
          const secondary = accounts[1];
          const others = accounts.slice(2).map(accountShort).join(', ');
          const site = getSiteLabel(row.item);
          const clientLabel = site && site !== 'General' ? `${row.client.name || ''} (${site})` : row.client.name || '';
          return {
            priority: currentEntry?.priority || 99,
            html: `<tr>
              <td>${escapeHtml(getGroupLabel(row.client))}</td>
              <td>${escapeHtml(clientLabel)}</td>
              <td>${escapeHtml(getPortfolioRowSector(row) || '')}</td>
              <td class="account-code">${escapeHtml(accountShort(primary))}</td>
              <td class="account-code">${escapeHtml(accountShort(secondary))}</td>
              <td>${escapeHtml(others)}</td>
            </tr>`,
          };
        }), sectionHeaders))
        .join('');
    };

    const tableRows = rows.map((row) => `<tr>${headers.map((header) => `<td>${escapeHtml(row[header])}</td>`).join('')}</tr>`).join('');
    const standardTable = `
      <table>
        <thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr></thead>
        <tbody>${tableRows || `<tr><td colspan="${headers.length || 1}">Aucune donnée</td></tr>`}</tbody>
      </table>`;
    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(getExportTitle(exportMode))}</title>
<style>
  body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
  h1 { font-size: 22px; margin: 0 0 6px; }
  .meta { color: #6b7280; font-size: 12px; margin-bottom: 16px; }
  table { border-collapse: collapse; width: 100%; font-size: 11px; }
  th { background: #e5e7eb; text-align: left; }
  th, td { border: 1px solid #111827; padding: 5px 7px; vertical-align: top; }
  tr:nth-child(even) td { background: #f9fafb; }
  .account-section { break-inside: avoid; margin: 0 0 18px; border: 2px solid #111827; }
  .account-title { background: #4472c4; color: white; font-weight: 800; text-align: center; padding: 5px 8px; }
  .portfolio-grid { font-size: 12px; }
  .portfolio-grid th { background: #ddebf7; text-align: center; font-weight: 700; }
  .portfolio-grid td { text-align: center; }
  .portfolio-grid td:nth-child(2) { text-align: left; }
  .account-code { font-weight: 800; }
  .category-row td { background: #f3f6fb !important; color: #1f4e79; font-weight: 800; text-align: center !important; }
  @media print { body { margin: 10mm; } .account-section { page-break-inside: avoid; } }
</style>
</head>
<body>
  <h1>${escapeHtml(getExportTitle(exportMode))}</h1>
  <div class="meta">${rows.length} ligne(s) - export ${new Date().toLocaleString('fr-FR')}</div>
  ${exportMode === 'account' ? renderAccountPdfBody() || '<p>Aucune donnée</p>' : standardTable}
</body>
</html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            {activeTab === 'contracts' ? <AssignmentTurnedInIcon color="primary" /> : <GroupsIcon color="primary" />}
            <Typography variant="h6" fontWeight={800}>Portefeuille</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Clients, sites et contrats.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {activeTab === 'contracts' ? (
            <>
              <Chip label={`${stats.totalContracts} contrats`} color="primary" variant="outlined" />
              <Chip label={`${stats.assignedContracts} affectes`} color={stats.assignedContracts ? 'success' : 'default'} variant="outlined" />
            </>
          ) : (
            <>
              <Chip label={`${stats.totalRows} lignes`} color="primary" variant="outlined" />
              <Chip label={`${stats.totalClients} clients`} variant="outlined" />
              <Chip label={`${stats.detailedRows} detaillees`} color={stats.detailedRows ? 'success' : 'default'} variant="outlined" />
            </>
          )}
          <Button
            startIcon={<FileDownloadIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            variant="outlined"
            onClick={(event) => setExportAnchorEl(event.currentTarget)}
            disabled={loading || !(activeTab === 'contracts' ? filteredContractRows.length : filteredRows.length)}
          >
            Exporter
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={() => setExportAnchorEl(null)}
          >
            <ListSubheader>CSV</ListSubheader>
            <MenuItem onClick={() => { exportCsv('current'); setExportAnchorEl(null); }}>Par {activeTab === 'contracts' ? 'contrat' : 'client'}</MenuItem>
            <MenuItem onClick={() => { exportCsv('account'); setExportAnchorEl(null); }}>Par compte</MenuItem>
            <ListSubheader>PDF</ListSubheader>
            <MenuItem onClick={() => { exportPdf('current'); setExportAnchorEl(null); }}>Par {activeTab === 'contracts' ? 'contrat' : 'client'}</MenuItem>
            <MenuItem onClick={() => { exportPdf('account'); setExportAnchorEl(null); }}>Par compte</MenuItem>
          </Menu>
          <Button startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>Actualiser</Button>
          {activeTab === 'clients' ? (
            <Button startIcon={<AddIcon />} variant="contained" onClick={openAssignment}>Ajouter</Button>
          ) : null}
        </Stack>
      </Stack>

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            label="Filtrer"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: { md: 320 } }}
          />
          <Autocomplete
            size="small"
            options={['all', ...rootOptions]}
            value={rootFilter}
            getOptionLabel={(option) => option === 'all' ? 'Toutes les sociétés' : option}
            onChange={(_, value) => setRootFilter(value || 'all')}
            sx={{ minWidth: { md: 220 } }}
            renderInput={(params) => <TextField {...params} label="Société" />}
          />
          <Autocomplete
            size="small"
            options={[{ value: 'all', label: 'Tous les clients' }, ...clientOptions]}
            value={[{ value: 'all', label: 'Tous les clients' }, ...clientOptions].find((option) => option.value === clientFilter) || null}
            getOptionLabel={(option) => option.label || option.name || 'Client'}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, value) => setClientFilter(value?.value || 'all')}
            sx={{ minWidth: { md: 260 } }}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
          <Autocomplete
            size="small"
            options={['all', ...SECTOR_OPTIONS]}
            value={sectorFilter}
            getOptionLabel={(option) => option === 'all' ? 'Tous les secteurs' : option}
            onChange={(_, value) => setSectorFilter(value || 'all')}
            sx={{ minWidth: { md: 190 } }}
            renderInput={(params) => <TextField {...params} label="Secteur" />}
          />
          <Autocomplete
            size="small"
            options={['all', ...countryOptions]}
            value={countryFilter}
            getOptionLabel={(option) => option === 'all' ? 'Tous les pays' : option}
            onChange={(_, value) => setCountryFilter(value || 'all')}
            sx={{ minWidth: { md: 190 } }}
            renderInput={(params) => <TextField {...params} label="Pays" />}
          />
          <Autocomplete
            size="small"
            options={[{ value: 'all', label: 'Tous les comptes', organizationLabel: 'TOUS' }, ...staffOptions]}
            value={[{ value: 'all', label: 'Tous les comptes', organizationLabel: 'TOUS' }, ...staffOptions].find((option) => option.value === managerFilter) || null}
            groupBy={(option) => option.organizationLabel || 'AUTRES'}
            getOptionLabel={(option) => option.label || getStaffLabel(option)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, value) => setManagerFilter(value?.value || 'all')}
            sx={{ minWidth: { md: 280 } }}
            renderInput={(params) => <TextField {...params} label="Compte" />}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: { md: 'auto' } }}>
            {activeTab === 'contracts' ? filteredContractRows.length : filteredRows.length} ligne{(activeTab === 'contracts' ? filteredContractRows.length : filteredRows.length) > 1 ? 's' : ''}
          </Typography>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5, mb: 2 }}>
        <PortfolioPieCard title="Clients par compte n°1" data={portfolioChartData.principal} />
        <PortfolioPieCard title="Clients par secteur" data={portfolioChartData.sector} />
        <PortfolioPieCard title="Clients par pays" data={portfolioChartData.country} />
        <PortfolioStackedBarCard data={portfolioChartData.accounts} />
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ px: 1 }}>
          <Tab value="clients" label="Portefeuille client" />
          <Tab value="contracts" label="Portefeuille Contrat" />
        </Tabs>
      </Paper>

      {activeTab === 'contracts' ? (
        filteredContractRows.length ? (
          <ContractPortfolioTable rows={filteredContractRows} onEdit={openContractEdit} onManagerClick={setManagerFilter} showRole={managerFilter !== 'all'} />
        ) : (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">Aucun contrat.</Typography>
          </Paper>
        )
      ) : (
        <Stack spacing={2}>
          {groupedPortfolios.map((group, index) => (
            <PortfolioMatrix
              key={group.id}
              title={group.title}
              user={group.user}
              primaryCount={group.primaryCount}
              secondaryCount={group.secondaryCount}
              rows={group.rows}
              color={group.id === 'unassigned' ? '#7f7f7f' : colorForIndex(index)}
              onEdit={openEdit}
              onClear={deleteItem}
              onManagerClick={setManagerFilter}
              editingSectorRowId={editingSectorRowId}
              onSectorEditStart={setEditingSectorRowId}
              onSectorChange={saveSectorInline}
              showRole={managerFilter !== 'all'}
            />
          ))}
          {!groupedPortfolios.length ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Aucune ligne portefeuille.</Typography>
            </Paper>
          ) : null}
        </Stack>
      )}

      <Dialog open={assignmentOpen} onClose={closeAssignment} fullWidth maxWidth="sm">
        <DialogTitle>{form.itemId ? 'Modifier une ligne' : 'Ajouter une ligne'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Autocomplete
              options={clientOptions}
              value={form.client}
              getOptionLabel={(option) => option.label || option.name || 'Sans nom'}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => {
                setForm({ ...form, client: value, site: null, siteName: '', portfolioSector: getSectorLabel(value || {}) });
                loadSites(value?.value);
              }}
              renderOption={(props, option) => {
                const existingCount = portfolioRows.filter((row) => row.client.value === option.value).length;
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" noWrap>{option.label || option.name || 'Sans nom'}</Typography>
                    {existingCount ? <Chip size="small" color="warning" variant="outlined" label="Déjà ajouté" /> : null}
                  </Box>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Client" size="small" />}
            />

            {selectedClientExistingRows.length ? (
              <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'rgba(245, 158, 11, 0.06)', borderColor: 'warning.light' }}>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip size="small" color="warning" label="Déjà ajouté" />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {selectedClientExistingRows.length} ligne{selectedClientExistingRows.length > 1 ? 's' : ''} existante{selectedClientExistingRows.length > 1 ? 's' : ''}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.75}>
                    {selectedClientExistingRows.slice(0, 4).map((row) => (
                      <Stack key={row.rowId} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" sx={{ fontWeight: 800 }}>{getSiteLabel(row.item)}</Typography>
                        {getPortfolioRowSector(row) ? <Chip size="small" variant="outlined" label={getPortfolioRowSector(row)} /> : null}
                        <Typography variant="caption" color="text.secondary">
                          Comptes: {getPortfolioAccounts(row.item.staffPortfolio).map((user, index) => `${index + 1}. ${getStaffLabel(user)}`).join(' / ') || '-'}
                        </Typography>
                      </Stack>
                    ))}
                    {selectedClientExistingRows.length > 4 ? (
                      <Typography variant="caption" color="text.secondary">+{selectedClientExistingRows.length - 4} autre{selectedClientExistingRows.length - 4 > 1 ? 's' : ''}</Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </Paper>
            ) : null}

            <Autocomplete
              options={siteOptions}
              value={form.site}
              loading={sitesLoading}
              getOptionLabel={(option) => option.label || option.name || 'Site'}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setForm({ ...form, site: value, siteName: value?.name || form.siteName })}
              renderInput={(params) => <TextField {...params} label="Site Hudu" size="small" />}
            />

            <TextField
              label="Site / périmètre"
              size="small"
              value={form.siteName}
              onChange={(event) => setForm({ ...form, siteName: event.target.value })}
              helperText="Libre si le site n'est pas dans Hudu"
            />

            <TextField
              select
              label="Secteur"
              size="small"
              value={form.portfolioSector || ''}
              onChange={(event) => setForm({ ...form, portfolioSector: event.target.value })}
            >
              <MenuItem value="">-</MenuItem>
              {SECTOR_OPTIONS.map((sector) => <MenuItem key={sector} value={sector}>{sector}</MenuItem>)}
            </TextField>

            <TextField
              label="Description"
              size="small"
              multiline
              minRows={2}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />

            <Autocomplete
              multiple
              options={staffOptions}
              value={form.accounts}
              getOptionLabel={(option) => option.label || getStaffLabel(option)}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setForm({ ...form, accounts: value })}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.value} size="small" label={`${index + 1}. ${option.label || getStaffLabel(option)}`} />
              ))}
              renderInput={(params) => <TextField {...params} label="Comptes par ordre" size="small" helperText="L'ordre des chips définit 1, 2, 3..." />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignment} disabled={saving}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
            onClick={saveItem}
            disabled={saving || !form.client || !form.accounts.length}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={contractAssignmentOpen} onClose={closeContractAssignment} fullWidth maxWidth="sm">
        <DialogTitle>{`Portefeuille Contrat - ${getContractLabel(contractForm.contract?.entity)}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Client"
              size="small"
              value={getOrganizationLabel(contractForm.contract)}
              InputProps={{ readOnly: true }}
            />

            <Autocomplete
              multiple
              options={staffOptions}
              value={contractForm.accounts}
              getOptionLabel={(option) => option.label || getStaffLabel(option)}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setContractForm({ ...contractForm, accounts: value })}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.value} size="small" label={`${index + 1}. ${option.label || getStaffLabel(option)}`} />
              ))}
              renderInput={(params) => <TextField {...params} label="Comptes contrat par ordre" size="small" helperText="L'ordre des chips définit 1, 2, 3..." />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContractAssignment} disabled={saving}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
            onClick={saveContractPortfolio}
            disabled={saving || !contractForm.contract || !contractForm.accounts.length}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
